import type { ServiceErrorOptions } from './_service.type';

import { BaseError } from './_base';

/** Service Error. */
export class ServiceError extends BaseError {
  /** Status Code. */
  status: number;

  /** Error Code. */
  code?: string | number;

  /**
   * Create ServiceError instance.
   *
   * @param {ServiceErrorOptions} options Options.
   */
  protected constructor({ status, code, ...options }: ServiceErrorOptions) {
    super(options);

    this.status = status;
    this.code = code;
  }

  /**
   * Create Error instance if cause is not Error.
   *
   * @param {ServiceErrorOptions} options Options.
   *
   * @returns {BaseError} Error instance.
   */
  static override create(options: ServiceErrorOptions): BaseError {
    if (options.cause instanceof BaseError) {
      return options.cause;
    }

    return new ServiceError(options);
  }

  /**
   * Create Not Found Error.
   *
   * @param {unknown} cause Cause of error.
   *
   * @returns {ServiceError} ServiceError.
   */
  static notFound(cause?: unknown): ServiceError {
    return new ServiceError({
      status: 404,
      message: 'Not Found',
      cause,
    });
  }

  /**
   * Create Forbidden Error.
   *
   * @param {unknown} cause Cause of error.
   *
   * @returns {ServiceError} ServiceError.
   */
  static forbidden(cause?: unknown): ServiceError {
    return new ServiceError({
      status: 403,
      message: 'Forbidden',
      cause,
    });
  }

  /**
   * Create Internal Server Error.
   *
   * @param {unknown} cause Cause of error.
   *
   * @returns {ServiceError} ServiceError.
   */
  static internalServerError(cause?: unknown): ServiceError {
    return new ServiceError({
      status: 500,
      message: 'Internal Server Error',
      cause,
    });
  }
}
